import React, { useEffect, useState } from 'react';
import { AlertTriangle, CheckCircle, ChevronRight, Clock, Eye, History, Tag, X } from 'lucide-react';
import Swal from 'sweetalert2';
import API_BASE from './apiBase';
import { compareConsentVersions } from './consentVersionUtils';

const nextVersion = (value) => {
  const parts = String(value || '1.0.0').replace(/^v/i, '').split('.').map(Number);
  parts[parts.length - 1] = (parts[parts.length - 1] || 0) + 1;
  return parts.join('.');
};

function VersionManagementModal({ item, mode = 'main', tenantId, token = '', onClose, onUpdated }) {
  const parsed = item?.parsed || {};
  const currentVersion = item?.version || item?.consent_version || parsed.version || parsed.consent_version || '1.0.0';
  const [step, setStep] = useState('current');
  const [newVersion, setNewVersion] = useState(nextVersion(currentVersion));
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState('');
  const base = API_BASE.replace(/\/$/, '');

  useEffect(() => {
    if (mode !== 'sub') {
      setHistory([{ version: currentVersion, setAt: item?.created_at || null, status: item?.status || 'active' }]);
      return;
    }

    const loadHistory = async () => {
      setHistoryLoading(true);
      setHistoryError('');
      try {
        const params = new URLSearchParams({ include_inactive: 'true' });
        if (tenantId != null) params.set('tenant_id', String(tenantId));
        const parentConsentId = item?.parent_consent_id || item?.consent_id || parsed.parent_consent_id || parsed.consent_id;
        if (parentConsentId != null) params.set('parent_consent_id', String(parentConsentId));
        const headers = {};
        if (token) headers.Authorization = `Bearer ${token}`;
        const response = await fetch(`${base}/manager/sub-category-consents?${params}`, { headers });
        const body = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(body?.detail || body?.message || `Failed to load version history (${response.status})`);

        const rows = Array.isArray(body) ? body : (Array.isArray(body?.rows) ? body.rows : []);
        const targetName = String(item?.sub_category || item?.consent_type || item?.name || '').trim().toLocaleLowerCase();
        const matchingHistory = rows
          .map(row => {
            let metadata = row?.parsed || row?.data || {};
            if (typeof metadata === 'string') {
              try { metadata = JSON.parse(metadata); } catch { metadata = {}; }
            }
            if (!metadata || typeof metadata !== 'object' || Array.isArray(metadata)) metadata = {};
            return { row, metadata };
          })
          .filter(({ row, metadata }) => {
            if (metadata.kind === 'multilingual_consent' || metadata.is_multilingual_variant) return false;
            const rowName = String(row?.sub_category || metadata.sub_consent_name || row?.consent_type || '').trim().toLocaleLowerCase();
            return rowName === targetName;
          })
          .map(({ row, metadata }) => ({
            version: row?.version || metadata.version || metadata.consent_version,
            setAt: row?.created_at || null,
            rejectedAt: metadata.rejected_at || metadata.rejectedAt || null,
            status: row?.status || metadata.status || 'under_process',
          }))
          .filter(entry => entry.version != null && String(entry.version).trim() !== '');

        const knownVersions = new Set(matchingHistory.map(entry => String(entry.version).replace(/^v/i, '').trim()));
        if (!knownVersions.has(String(currentVersion).replace(/^v/i, '').trim())) {
          matchingHistory.push({
            version: currentVersion,
            setAt: item?.created_at || null,
            status: item?.status || 'under_process',
          });
        }
        const uniqueHistory = Array.from(new Map(
          matchingHistory.map(entry => [String(entry.version).replace(/^v/i, '').trim(), entry])
        ).values());
        uniqueHistory.sort((left, right) => compareConsentVersions(right.version, left.version));
        setHistory(uniqueHistory);
      } catch (error) {
        setHistoryError(error.message || 'Failed to load version history');
      } finally {
        setHistoryLoading(false);
      }
    };

    loadHistory();
  }, [base, currentVersion, item, mode, parsed, tenantId, token]);

  const request = async (url, payload) => {
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;
    const response = await fetch(`${base}${url}`, { method: 'POST', headers, body: JSON.stringify(payload) });
    const body = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(body?.detail || body?.message || 'Version update failed');
    return body;
  };

  const rejectOld = async () => {
    setBusy(true);
    try {
      if (mode === 'sub') {
        await request('/manager/reject-subcategory-version', { id: item.id, tenant_id: tenantId });
      } else {
        await request('/manager/reject-consent-version', { id: item.id, tenant_id: tenantId, consent_type: item.consent_type || item.type || '', consent_version: currentVersion });
      }
      setStep('new');
    } catch (error) {
      await Swal.fire({ icon: 'error', title: 'Reject Failed', text: error.message });
    } finally {
      setBusy(false);
    }
  };

  const createNew = async () => {
    const candidate = newVersion.trim();
    if (!candidate || compareConsentVersions(candidate, currentVersion) !== 1) {
      await Swal.fire({ icon: 'error', title: 'Invalid Version', text: `Enter a version greater than ${currentVersion}.` });
      return;
    }
    setBusy(true);
    try {
      if (mode === 'sub') {
        // const data = { ...parsed, version: candidate, status: 'under_process', locked: false, rejected_at: null };
        const parentConsentId = item.parent_consent_id || item.consent_id || parsed.parent_consent_id || parsed.consent_id;
        const data = {
          ...parsed,
          parent_consent_id: parentConsentId,
          consent_id: parentConsentId,
          version: candidate,
          status: 'under_process',
          locked: false,
          rejected_at: null,
        };
        await request('/manager/create-subcategory', {
          tenant_id: tenantId,
          sub_category: item.sub_category || item.consent_type,
          name: item.name || item.consent_type,
          consent_type: item.consent_type || item.name,
          parent_consent_type: parsed.parent_consent_type || item.consent_type || item.name,
          parent_consent_id: parentConsentId,
          consent_id: parentConsentId,
          previous_sub_consent_id: item.id,
          version_update: true,
          message: item.consent_message || item.message || '',
          version: candidate,
          data: JSON.stringify(data),
        });
      } else {
        await request('/manager/new-consent', {
          tenant_id: tenantId,
          consent_type: item.consent_type || item.type,
          consent_message: item.consent_message || item.message || parsed.consent_message || '',
          consent_metadata: JSON.stringify({ ...parsed, version: candidate, consent_version: candidate }),
          consent_version: candidate,
          version: candidate,
          version_update: true,
          language_code: item.language_code || parsed.language_code,
          language_name: item.language_name || parsed.language_name,
          parent_id: item.parent_id || parsed.parent_id,
          kind: item.kind || parsed.kind,
          parent_consent_type: parsed.parent_consent_type,
          parent_consent_label: parsed.parent_consent_label,
        });
        if (item.id) {
          try {
            await fetch(`${base}/manager/privacy-notices/${item.id}`, {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
              body: JSON.stringify({
                status: 'under_process',
                locked: false,
                version: candidate,
                consent_version: candidate,
                card_data: { version: candidate, status: 'under_process', locked: false }
              })
            });
          } catch (e) {
            console.warn('Failed to sync privacy notice version update', e);
          }
        }
      }
      await Swal.fire({ icon: 'success', title: 'Version Updated', text: `v${candidate} is now Under Process.`, timer: 1600, showConfirmButton: false });
      if (onUpdated) await onUpdated(candidate);
      onClose();
    } catch (error) {
      await Swal.fire({ icon: 'error', title: 'Version Update Failed', text: error.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 2100, background: 'rgba(15,23,42,0.48)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div role="dialog" aria-modal="true" style={{ width: '100%', maxWidth: 680, maxHeight: '92vh', overflow: 'auto', background: '#fff', borderRadius: 18, boxShadow: '0 24px 80px rgba(15,23,42,0.28)' }}>
        <div style={{ background: 'linear-gradient(135deg,#1d4ed8,#3b82f6)', color: '#fff', padding: '22px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div><div style={{ fontSize: 19, fontWeight: 800 }}>Version Management</div><div style={{ opacity: .8, marginTop: 4 }}>{item?.consent_type || item?.sub_category || item?.name || 'Consent'}</div></div>
          <button onClick={onClose} aria-label="Close" style={{ background: 'rgba(255,255,255,.16)', border: 0, color: '#fff', borderRadius: 10, padding: 10, cursor: 'pointer' }}><X size={20} /></button>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '18px 24px', background: '#f8fafc' }}>
          {[['current', '1. Current', Eye], ['reject', '2. Reject Old', AlertTriangle], ['new', '3. Set New', CheckCircle]].map(([key, label, Icon], index) => <React.Fragment key={key}><div style={{ padding: '9px 14px', borderRadius: 999, background: step === key ? '#2563eb' : '#e2e8f0', color: step === key ? '#fff' : '#94a3b8', fontWeight: 700, fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}><Icon size={14} />{label}</div>{index < 2 && <ChevronRight size={18} color="#cbd5e1" />}</React.Fragment>)}
        </div>
        <div style={{ padding: 24 }}>
          {step === 'current' && <>
            <div style={{ border: '1.5px solid #bfdbfe', background: 'linear-gradient(135deg,#eff6ff,#dbeafe)', borderRadius: 14, padding: 22, display: 'flex', alignItems: 'center', gap: 16 }}><Tag size={38} color="#2563eb" /><div><div style={{ color: '#64748b', fontSize: 12, fontWeight: 700, textTransform: 'uppercase' }}>Current Version</div><div style={{ color: '#1e3a8a', fontSize: 34, fontWeight: 800 }}>v{currentVersion}</div></div><span style={{ marginLeft: 'auto', color: '#047857', background: '#d1fae5', border: '1px solid #6ee7b7', borderRadius: 999, padding: '6px 12px', fontWeight: 700 }}>Active</span></div>
            <div style={{ marginTop: 20, border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden' }}>
              <div style={{ padding: '12px 14px', fontWeight: 700, display: 'flex', gap: 8, alignItems: 'center' }}><History size={16} color="#6366f1" />Version History</div>
              {historyLoading && <div style={{ padding: '12px 14px', color: '#64748b' }}>Loading version history...</div>}
              {historyError && <div role="alert" style={{ padding: '12px 14px', color: '#b91c1c' }}>{historyError}</div>}
              {!historyLoading && !historyError && history.map(entry => <div key={entry.version} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', padding: '12px 14px', borderTop: '1px solid #f1f5f9', fontSize: 12 }}>
                <span><Tag size={11} color="#2563eb" /> v{entry.version}</span>
                <span><Clock size={11} color="#94a3b8" /> {entry.setAt ? new Date(entry.setAt).toLocaleString() : '—'}</span>
                <span style={{ textTransform: 'capitalize' }}>{entry.status || (entry.rejectedAt ? 'rejected' : '—')}</span>
              </div>)}
            </div>
          </>}
          {step === 'reject' && <div style={{ padding: 20, border: '1px solid #fed7aa', background: '#fff7ed', borderRadius: 14 }}><h3 style={{ marginTop: 0, color: '#9a3412' }}>Reject Current Version First</h3><p style={{ color: '#92400e' }}>Version <strong>v{currentVersion}</strong> will be rejected before the new version is created.</p></div>}
          {step === 'new' && <div><div style={{ padding: 16, border: '1px solid #86efac', background: '#f0fdf4', borderRadius: 12, color: '#166534', fontWeight: 700 }}>Old version rejected. Set a higher version below.</div><label style={{ display: 'block', marginTop: 18, marginBottom: 7, fontWeight: 700 }}>New Version</label><input autoFocus value={newVersion} onChange={event => setNewVersion(event.target.value)} onKeyDown={event => { if (event.key === 'Enter') createNew(); }} style={{ width: '100%', boxSizing: 'border-box', padding: 13, border: '2px solid #bfdbfe', borderRadius: 10, fontSize: 16, fontWeight: 700 }} /><div style={{ marginTop: 6, color: '#64748b', fontSize: 12 }}>Must be greater than v{currentVersion}.</div></div>}
        </div>
        <div style={{ display: 'flex', gap: 10, padding: '16px 24px', borderTop: '1px solid #e2e8f0', background: '#fafafa' }}>
          {step === 'current' && <><button onClick={() => setStep('reject')} style={{ flex: 1, padding: 13, border: 0, borderRadius: 10, background: '#2563eb', color: '#fff', fontWeight: 700, cursor: 'pointer' }}><Tag size={16} /> Update Version</button><button onClick={onClose} style={{ padding: '13px 22px', border: '1px solid #cbd5e1', borderRadius: 10, background: '#fff', cursor: 'pointer' }}>Close</button></>}
          {step === 'reject' && <><button disabled={busy} onClick={rejectOld} style={{ flex: 1, padding: 13, border: 0, borderRadius: 10, background: '#dc2626', color: '#fff', fontWeight: 700, cursor: 'pointer' }}>{busy ? 'Rejecting...' : 'Confirm Reject Old Version'}</button><button disabled={busy} onClick={() => setStep('current')} style={{ padding: '13px 22px', border: '1px solid #cbd5e1', borderRadius: 10, background: '#fff', cursor: 'pointer' }}>Back</button></>}
          {step === 'new' && <><button disabled={busy} onClick={createNew} style={{ flex: 1, padding: 13, border: 0, borderRadius: 10, background: '#059669', color: '#fff', fontWeight: 700, cursor: 'pointer' }}>{busy ? 'Applying...' : 'Apply New Version'}</button><button disabled={busy} onClick={onClose} style={{ padding: '13px 22px', border: '1px solid #cbd5e1', borderRadius: 10, background: '#fff', cursor: 'pointer' }}>Cancel</button></>}
        </div>
      </div>
    </div>
  );
}

export default VersionManagementModal;
